  import React, { useState } from "react";
  import styled from "styled-components";

  import btnSMS from "../../assets/MentalPlus/btnSMS.png";
  import btnSMSNoir from "../../assets/MentalPlus/btnSMSNoir.png";
  import btnSuppr from "../../assets/MentalPlus/BtnSuppr.png";
  import btnTel from "../../assets/MentalPlus/btnTel.png";
  import btnTelNoir from "../../assets/MentalPlus/btnTelNoir.png";
  import iconModify from "../../assets/MentalPlus/Modify.png";

  import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
  import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";

  import {
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonCheckbox,
    IonToggle,
    useIonViewWillEnter,
    useIonViewWillLeave,
  } from "@ionic/react";

  /* -------------------------------------------------------------------------- */
  /*                                 TYPES                                       */
  /* -------------------------------------------------------------------------- */

  type EmergencyData = {
    pinned: string; // "Titre – Numéro"
    urgences: string[];
    proches: string[];
  };

  type EmergencyCategory = "urgences" | "proches";
  type MainTab = "urgence" | "signes" | "activites";

  /* -------------------------------------------------------------------------- */
  /*                          NUMÉROS DE SOUTIENS CARDS                         */
  /* -------------------------------------------------------------------------- */

  function EmergencyCards() {
    const defaultData: EmergencyData = {
      pinned: "Prévention suicide – 3114",
      urgences: [
        "Prévention suicide – 3114",
        "Urgence vitale – 112",
        "Croix-Rouge écoute – 0 800 858 858",
        "SOS Amitié – 09 72 39 40 50",
        "SOS crise – 0800 19 00 00",
      ],
      proches: ["Papa – 06 89 76 54 36"],
    };

    const [emergencyData, setEmergencyData] = useState<EmergencyData>(() => {
      const stored = localStorage.getItem("emergencyDataV2");
      return stored ? JSON.parse(stored) : defaultData;
    });

    const updateStorage = (newData: EmergencyData) => {
      setEmergencyData(newData);
      localStorage.setItem("emergencyDataV2", JSON.stringify(newData));
    };

    const splitLine = (line: string) => {
      const [title, number] = line.split("–");
      return {
        title: title?.trim() ?? "",
        number: number?.trim() ?? "",
      };
    };

    const sortAlphabetically = (list: string[]) => {
      return [...list].sort((a, b) => {
        const titleA = a.split("–")[0].trim().toLowerCase();
        const titleB = b.split("–")[0].trim().toLowerCase();
        return titleA.localeCompare(titleB, "fr");
      });
    };

    /* ------------------------------- PINNED --------------------------------- */

    const [isPinnedPickerOpen, setIsPinnedPickerOpen] = useState(false);
    const [pinnedSource, setPinnedSource] =
      useState<"urgences" | "proches" | null>(null);
    const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);

    const openPinnedPicker = () => {
      const current = emergencyData.pinned;
      let foundSource: "urgences" | "proches" | null = null;
      let foundIndex: number | null = null;

      const allUrgences = emergencyData.urgences || [];
      const allProches = emergencyData.proches || [];

      const idxUrg = allUrgences.indexOf(current);
      if (idxUrg !== -1) {
        foundSource = "urgences";
        foundIndex = idxUrg;
      } else {
        const idxPro = allProches.indexOf(current);
        if (idxPro !== -1) {
          foundSource = "proches";
          foundIndex = idxPro;
        }
      }

      setPinnedSource(foundSource);
      setPinnedIndex(foundIndex);
      setIsPinnedPickerOpen(true);
    };

    /* --------------------------- PROCHES : EDITION -------------------------- */

    const [isEditProchesOpen, setIsEditProchesOpen] = useState(false);
    const [manualName, setManualName] = useState("");
    const [manualPhone, setManualPhone] = useState("");
    const [isAddingProche, setIsAddingProche] = useState(false);

    const openProchesEditor = () => {
      setIsEditProchesOpen(true);
      setIsAddingProche(false);
      setManualName("");
      setManualPhone("");
    };

    const closeProchesEditor = () => {
      setIsEditProchesOpen(false);
      setIsAddingProche(false);
      setManualName("");
      setManualPhone("");
    };

    const addManualProche = () => {
      if (!manualName.trim() || !manualPhone.trim()) return;

      const line = `${manualName.trim()} – ${manualPhone.trim()}`;
      const updatedProches = [...emergencyData.proches, line];

      updateStorage({
        ...emergencyData,
        proches: updatedProches,
      });

      setManualName("");
      setManualPhone("");
      setIsAddingProche(false);
    };

    const removeProche = (indexToRemove: number) => {
      const updatedProches = emergencyData.proches.filter(
        (_, idx) => idx !== indexToRemove
      );

      let newPinned = emergencyData.pinned;
      const removedLine = emergencyData.proches[indexToRemove];
      if (removedLine === emergencyData.pinned) {
        if (updatedProches.length > 0) newPinned = updatedProches[0];
        else if (emergencyData.urgences.length > 0)
          newPinned = emergencyData.urgences[0];
        else newPinned = "";
      }

      updateStorage({
        ...emergencyData,
        proches: updatedProches,
        pinned: newPinned,
      });
    };

    /* -------------------------- URGENCES : EDITION -------------------------- */

    const [isEditUrgencesOpen, setIsEditUrgencesOpen] = useState(false);

    const [activeUrgenceIndexes, setActiveUrgenceIndexes] = useState<number[]>(
      () => {
        const stored = localStorage.getItem("activeUrgenceIndexes");
        if (!stored) return [];
        try {
          const parsed = JSON.parse(stored);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
    );

    const [selectedUrgences, setSelectedUrgences] = useState<number[]>([]);

    const openUrgencesEditor = () => {
      const allIndexes = emergencyData.urgences.map((_, idx) => idx);
      const initialSelection =
        activeUrgenceIndexes.length > 0 ? activeUrgenceIndexes : allIndexes;

      setSelectedUrgences(initialSelection);
      setIsEditUrgencesOpen(true);
    };

    const toggleUrgenceSelection = (index: number) => {
      setSelectedUrgences((prev) =>
        prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
      );
    };

    const saveUrgencesSelection = () => {
      setActiveUrgenceIndexes(selectedUrgences);
      localStorage.setItem(
        "activeUrgenceIndexes",
        JSON.stringify(selectedUrgences)
      );
      setIsEditUrgencesOpen(false);
    };

    /* ------------------------------ RENDU LISTES ---------------------------- */

    const renderListSection = (
      title: string,
      category: EmergencyCategory,
      withEditIcon = false,
      onEdit?: () => void
    ) => {
      const list = sortAlphabetically(emergencyData[category]);

      let indexesToShow: number[];

      if (category === "urgences") {
        if (activeUrgenceIndexes.length === 0) {
          indexesToShow = list.map((_, idx) => idx);
        } else {
          indexesToShow = activeUrgenceIndexes.filter(
            (i) => i >= 0 && i < list.length
          );
        }
      } else {
        indexesToShow = list.map((_, idx) => idx);
      }

      return (
        <SectionBlock>
          <SectionHeaderRow>
            <SectionLabel>{title}</SectionLabel>

            {withEditIcon && (
              <SectionIconButton aria-label="Modifier la section" onClick={onEdit}>
                <HeaderIconImg
                  src={iconModify}
                  alt="Modifier"
                  style={{ pointerEvents: "none" }}
                />
              </SectionIconButton>
            )}
          </SectionHeaderRow>

          <CardsColumn>
            {indexesToShow.map((index) => {
              const line = list[index];
              const { title, number } = splitLine(line);

              return (
                <SupportCard key={`${category}-${index}`}>
                  <SupportInfo>
                    <SupportTitle>{title}</SupportTitle>
                    {number && <SupportNumber>{number}</SupportNumber>}
                  </SupportInfo>

                  <SupportActions>
                    <IconImgButton
                      aria-label="Appeler"
                      onClick={() => {
                        if (!number) return;
                        const cleanNumber = number.replace(/[^\d+]/g, "");
                        window.location.href = `tel:${cleanNumber}`;
                      }}
                    >
                      <IconImg src={btnTel} alt="Appeler" />
                    </IconImgButton>

                    <IconImgButton
                      aria-label="Envoyer un SMS"
                      onClick={() => {
                        if (!number) return;
                        const cleanNumber = number.replace(/[^\d+]/g, "");
                        window.location.href = `sms:${cleanNumber}`;
                      }}
                    >
                      <IconImg src={btnSMS} alt="SMS" />
                    </IconImgButton>
                  </SupportActions>
                </SupportCard>
              );
            })}
          </CardsColumn>
        </SectionBlock>
      );
    };

    const { title: pinnedTitle, number: pinnedNumber } = splitLine(
      emergencyData.pinned
    );

    return (
      <EmergencyWrapper>
        {/* Numéro épinglé */}
        <SectionBlock>
          <SectionHeaderRow>
            <SectionLabel>Numéro épinglé</SectionLabel>

            {/* 📌 remplacé par modify.png */}
            <SectionIconButton
              aria-label="Changer le numéro épinglé"
              onClick={openPinnedPicker}
            >
              <HeaderIconImg
                src={iconModify}

                alt="Changer le numéro épinglé"
                style={{ pointerEvents: "none" }}
              />
            </SectionIconButton>
          </SectionHeaderRow>

          <PinnedCard>
            <PinnedInfo>
              <PinnedTitle>{pinnedTitle}</PinnedTitle>
              {pinnedNumber && <PinnedNumber>{pinnedNumber}</PinnedNumber>}
            </PinnedInfo>

            <PinnedActions>
              <IconImgButton
                aria-label="Appeler"
                onClick={() => {
                  if (!pinnedNumber) return;
                  const cleanNumber = pinnedNumber.replace(/[^\d+]/g, "");
                  window.location.href = `tel:${cleanNumber}`;
                }}
              >
                <IconImg src={btnTelNoir} alt="Appeler" />
              </IconImgButton>

              <IconImgButton
                aria-label="Envoyer un SMS"
                onClick={() => {
                  if (!pinnedNumber) return;
                  const cleanNumber = pinnedNumber.replace(/[^\d+]/g, "");
                  window.location.href = `sms:${cleanNumber}`;
                }}
              >
                <IconImg src={btnSMSNoir} alt="SMS" />
              </IconImgButton>
            </PinnedActions>
          </PinnedCard>
        </SectionBlock>

        {renderListSection("Numéros d’urgence", "urgences", true, openUrgencesEditor)}
        {renderListSection("Proches", "proches", true, openProchesEditor)}

        {/* Sheet choix du numéro épinglé */}
        {isPinnedPickerOpen && (
          <BottomSheet>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "2vh",
              }}
            >
              Choisir le numéro épinglé
            </h3>

            <SheetCloseButton onClick={() => setIsPinnedPickerOpen(false)}>
              <SheetCloseImg src={btnSuppr} alt="Fermer" />
            </SheetCloseButton>

            <ProchesListBlock>
              {emergencyData.urgences.length + emergencyData.proches.length > 0 ? (
                <>
                  {[
                    ...emergencyData.urgences.map((line, idx) => ({
                      line,
                      source: "urgences" as const,
                      index: idx,
                    })),
                    ...emergencyData.proches.map((line, idx) => ({
                      line,
                      source: "proches" as const,
                      index: idx,
                    })),
                  ].map((item) => {
                    const { title, number } = splitLine(item.line);
                    const checked =
                      pinnedSource === item.source && pinnedIndex === item.index;

                    return (
                      <ProcheRow key={`${item.source}-${item.index}`}>
                      <ProcheInfo>
                        <span className="name">{title}</span>
                        {number && <span className="phone">{number}</span>}
                      </ProcheInfo>

                      <CheckboxWrapper>
                        <IonCheckbox
                          checked={checked}
                          onIonChange={() => {
                            setPinnedSource(item.source);
                            setPinnedIndex(item.index);
                          }}
                        />
                      </CheckboxWrapper>
                    </ProcheRow>

                    );
                  })}
                </>
              ) : (
                <ProchesEmpty>Aucun numéro disponible.</ProchesEmpty>
              )}
            </ProchesListBlock>

            <SaveButton
              onClick={() => {
                if (!pinnedSource || pinnedIndex === null) {
                  setIsPinnedPickerOpen(false);
                  return;
                }

                const list =
                  pinnedSource === "urgences"
                    ? emergencyData.urgences
                    : emergencyData.proches;

                if (!list[pinnedIndex]) {
                  setIsPinnedPickerOpen(false);
                  return;
                }

                updateStorage({ ...emergencyData, pinned: list[pinnedIndex] });
                setIsPinnedPickerOpen(false);
              }}
            >
              Valider ce numéro
            </SaveButton>
          </BottomSheet>
        )}

        {/* Sheet gestion des urgences */}
        {isEditUrgencesOpen && (
          <BottomSheet>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "2vh",
              }}
            >
              Gérer mes numéros d’urgence
            </h3>

            <SheetCloseButton onClick={() => setIsEditUrgencesOpen(false)}>
              <SheetCloseImg src={btnSuppr} alt="Fermer" />
            </SheetCloseButton>

            <ProchesListBlock>
              {emergencyData.urgences.length > 0 ? (
                sortAlphabetically(emergencyData.urgences).map((line, idx) => {
                  const { title, number } = splitLine(line);
                  const checked = selectedUrgences.includes(idx);

                  return (
                    <ProcheRow key={idx}>
                      <ProcheInfo>
                        <span className="name">{title}</span>
                        {number && <span className="phone">{number}</span>}
                      </ProcheInfo>
                      <ToggleWrapper>
                        <IonToggle
                          checked={checked}
                          onIonChange={() => toggleUrgenceSelection(idx)}
                        />
                      </ToggleWrapper>
                    </ProcheRow>
                  );
                })
              ) : (
                <ProchesEmpty>Aucun numéro d’urgence défini.</ProchesEmpty>
              )}
            </ProchesListBlock>

            <SaveButton onClick={saveUrgencesSelection}>Enregistrer</SaveButton>
          </BottomSheet>
        )}

        {/* Sheet gestion des proches */}
        {isEditProchesOpen && (
          <BottomSheet>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "2vh",
              }}
            >
              Gérer mes proches
            </h3>

            <SheetCloseButton onClick={closeProchesEditor}>
              <SheetCloseImg src={btnSuppr} alt="Fermer" />
            </SheetCloseButton>

            <ProchesListBlock>
              {emergencyData.proches.length > 0 ? (
                sortAlphabetically(emergencyData.proches).map((line, idx) => {
                  const { title, number } = splitLine(line);

                  return (
                    <SupportCard key={idx}>
                      <SupportInfo>
                        <SupportTitle>{title}</SupportTitle>
                        {number && <SupportNumber>{number}</SupportNumber>}
                      </SupportInfo>

                      <SupportActions>
                        <IconImgButton
                          aria-label="Supprimer ce proche"
                          onClick={() => removeProche(idx)}
                        >
                          <IconImg src={btnSuppr} alt="Supprimer" />
                        </IconImgButton>
                      </SupportActions>
                    </SupportCard>
                  );
                })
              ) : (
                <ProchesEmpty>Aucun proche enregistré pour le moment.</ProchesEmpty>
              )}
            </ProchesListBlock>

            {!isAddingProche && (
              <AddProcheToggleButton
                type="button"
                onClick={() => setIsAddingProche(true)}
              >
                Ajouter un proche
              </AddProcheToggleButton>
            )}

            {isAddingProche && (
              <AddBlock>
                <Input
                  type="text"
                  placeholder="Nom du proche (ex : Papa)"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                />
                <Input
                  type="tel"
                  placeholder="Numéro de téléphone"
                  value={manualPhone}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/[^\d+]/g, "");
                    setManualPhone(onlyNumbers);
                  }}
                />
                <AddProcheButton onClick={addManualProche}>
                  Ajouter ce proche
                </AddProcheButton>
              </AddBlock>
            )}
          </BottomSheet>
        )}
      </EmergencyWrapper>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                                SIGNES CARDS                                */
  /* -------------------------------------------------------------------------- */

  type SymptomCardsProps = {
    isAddingSignes: boolean;
    setIsAddingSignes: React.Dispatch<React.SetStateAction<boolean>>;
  };

  function SymptomCards({ isAddingSignes, setIsAddingSignes }: SymptomCardsProps) {
    const defaultCards = [
      "J’ai des troubles de l’appétit",
      "J’ai des troubles du sommeil",
      "Je suis anxieux.se",
      "Je me sens triste",
      "Je me sens inutile et vide",
      "Je me sens seul.e",
    ];

    const [cards, setCards] = useState<string[]>(() => {
      const stored = localStorage.getItem("symptomCards");
      return stored ? JSON.parse(stored) : defaultCards;
    });

    const [newSign, setNewSign] = useState("");

    const updateStorage = (newCards: string[]) => {
      setCards(newCards);
      localStorage.setItem("symptomCards", JSON.stringify(newCards));
    };

    const removeCard = (indexToRemove: number) => {
      const newCards = cards.filter((_, index) => index !== indexToRemove);
      updateStorage(newCards);
    };

    const addCard = () => {
      if (newSign.trim() !== "") {
        updateStorage([...cards, newSign.trim()]);
        setNewSign("");
        setIsAddingSignes(false);
      }
    };

    return (
      <>
        <SimpleCardsColumn>
          {cards.map((text, index) => (
            <SupportCard key={index}>
              <SupportInfo>
                <SimpleTitle>{text}</SimpleTitle>
              </SupportInfo>

              {/* ✅ croix en image */}
              <SupportActions>
                <IconImgButton
                  aria-label="Supprimer"
                  onClick={() => removeCard(index)}
                >
                  <IconImg src={btnSuppr} alt="Supprimer" />
                </IconImgButton>
              </SupportActions>
            </SupportCard>
          ))}
        </SimpleCardsColumn>

        {isAddingSignes && (
          <BottomSheet>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "6vh",
              }}
            >
              Ajouter un nouveau signe
            </h3>

            <SheetCloseButton onClick={() => setIsAddingSignes(false)}>
              <SheetCloseImg src={btnSuppr} alt="Fermer" />
            </SheetCloseButton>

            <Input
              type="text"
              value={newSign}
              onChange={(e) => setNewSign(e.target.value)}
            />

            <AddConfirmButton onClick={addCard}>Ajouter</AddConfirmButton>
          </BottomSheet>
        )}
      </>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                               ACTIVITÉS CARDS                              */
  /* -------------------------------------------------------------------------- */

  type ActivityCardsProps = {
    isAdding: boolean;
    setIsAdding: React.Dispatch<React.SetStateAction<boolean>>;
  };

  function ActivityCards({ isAdding, setIsAdding }: ActivityCardsProps) {
    const defaultActivities = [
      "Faire du yoga, se relaxer",
      "Écouter de la musique",
      "Regarder une vidéo",
    ];

    const [activities, setActivities] = useState<string[]>(() => {
      const stored = localStorage.getItem("activityCards");
      return stored ? JSON.parse(stored) : defaultActivities;
    });

    const [newActivity, setNewActivity] = useState("");

    const updateStorage = (newCards: string[]) => {
      setActivities(newCards);
      localStorage.setItem("activityCards", JSON.stringify(newCards));
    };

    const removeCard = (indexToRemove: number) => {
      const newCards = activities.filter((_, index) => index !== indexToRemove);
      updateStorage(newCards);
    };

    const addCard = () => {
      if (newActivity.trim() !== "") {
        updateStorage([...activities, newActivity.trim()]);
        setNewActivity("");
        setIsAdding(false);
      }
    };

    return (
      <>
        <SimpleCardsColumn>
          {activities.map((text, index) => (
            <SupportCard key={index}>
              <SupportInfo>
                <SimpleTitle>{text}</SimpleTitle>
              </SupportInfo>

              {/* ✅ croix en image */}
              <SupportActions>
                <IconImgButton
                  aria-label="Supprimer"
                  onClick={() => removeCard(index)}
                >
                  <IconImg src={btnSuppr} alt="Supprimer" />
                </IconImgButton>
              </SupportActions>
            </SupportCard>
          ))}
        </SimpleCardsColumn>

        {isAdding && (
          <BottomSheet>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "6vh",
              }}
            >
              Ajouter une solution
            </h3>

            <SheetCloseButton onClick={() => setIsAdding(false)}>
              <SheetCloseImg src={btnSuppr} alt="Fermer" />
            </SheetCloseButton>

            <Input
              type="text"
              value={newActivity}
              onChange={(e) => setNewActivity(e.target.value)}
            />

            <AddConfirmButton onClick={addCard}>Ajouter</AddConfirmButton>
          </BottomSheet>
        )}
      </>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                              COMPOSANT PRINCIPAL                           */
  /* -------------------------------------------------------------------------- */

  export default function HomePage() {
    const [activeTab, setActiveTab] = useState<MainTab>(() => {
      const stored = localStorage.getItem("activeTab");
      return (stored as MainTab) || "urgence";
    });

    const [isAddingSignes, setIsAddingSignes] = useState(false);
    const [isAddingActivites, setIsAddingActivites] = useState(false);

    const { setResonanceLayout } = useResonanceLayout();

    useIonViewWillEnter(() => {
      setResonanceLayout({
        disableHeader: false,
        disableNav: false,
        backgroundColor: "white",
        title: "Ma bouée",
        welcomePage: false,
        headerBackground: "white",
      });
    });

    useIonViewWillLeave(() => {
      setResonanceLayout({
        disableHeader: false,
        disableNav: false,
        backgroundColor: "white",
        welcomePage: false,
        headerBackground: "transparent",
      });
    });

    return (
      <ResonanceLayout isConnected>
        <PageWrapper>
          <TabsWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                background: "white",
                zIndex: 1000,
                padding: "8px 12px",
              }}
            >
              <StyledSegment
                value={activeTab}
                onIonChange={(e) => {
                  const tab = (e.detail.value as MainTab) || "urgence";
                  setActiveTab(tab);
                  localStorage.setItem("activeTab", tab);
                }}
              >
                <IonSegmentButton value="urgence">
                  <IonLabel>Numéros de soutiens</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="signes">
                  <IonLabel>Signes</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="activites">
                  <IonLabel>Solutions</IonLabel>
                </IonSegmentButton>
              </StyledSegment>
            </div>

            <HeaderSlot>
              {activeTab === "signes" && (
                <SectionHeader>
                  <QuestionHeading>
                    Quels sont les signes qui me font prendre conscience que je
                    commence à aller mal ?
                  </QuestionHeading>
                  <AddButton onClick={() => setIsAddingSignes(true)}>+</AddButton>
                </SectionHeader>
              )}

              {activeTab === "activites" && (
                <SectionHeader>
                  <QuestionHeading>
                    Quelles solutions peuvent m’aider à aller mieux ?
                  </QuestionHeading>
                  <AddButton onClick={() => setIsAddingActivites(true)}>
                    +
                  </AddButton>
                </SectionHeader>
              )}
            </HeaderSlot>
          </TabsWrapper>
{activeTab === "signes" && (
  <ContentSimple>
    <SymptomCards
      isAddingSignes={isAddingSignes}
      setIsAddingSignes={setIsAddingSignes}
    />
  </ContentSimple>
)}

{activeTab === "activites" && (
  <ContentSimple>
    <ActivityCards
      isAdding={isAddingActivites}
      setIsAdding={setIsAddingActivites}
    />
  </ContentSimple>
)}

{activeTab === "urgence" && (
  <Content>
    <EmergencyCards />
  </Content>
)}

        </PageWrapper>
      </ResonanceLayout>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                                   STYLES                                   */
  /* -------------------------------------------------------------------------- */

  const HeaderSlot = styled.div`
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: 100vw;
      background: #fff;
      z-index: -1;
      pointer-events: none;
    }
  `;

  const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-width: 600px;
    margin: 0 auto;
    background: white;
    font-family: sans-serif;
  `;

  const TabsWrapper = styled.div`
    display: flex;
    position: fixed;
    flex-direction: column;
    align-self: center;
    background: white;
    z-index: 10000;
  `;

 const Content = styled.div`
  flex: 1;
  padding: 24px 1rem 120px;
  padding-top: 70px;
  font-size: 16px;
    @media (max-width: 360px) {
    padding-top: 50px;
  }
`;  

const ContentSimple = styled.div`
  flex: 1;
  padding: 24px 1rem 120px;
  padding-top: 130px;
  font-size: 16px;
   @media (max-width: 360px) {
    padding-top: 110px;
  }
`;
  const SimpleCardsColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
  `;

  /* ----------------------------- Header Signes/Act -------------------------- */

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
  margin-top: 10px;
  flex-wrap: nowrap;
`;
 const QuestionHeading = styled.h2`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;           
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;
  word-break: break-word;
  padding-left: 12px;   
  padding-right: 8px;  

  @media (max-width: 360px) {
    font-size: 13px;
      
  }
`;
  const AddButton = styled.button`
  flex: 0 0 auto;    
  align-self: flex-start;

  background-color: #4285f4;
  color: white;
  font-size: 22px;
  border: none;
  border-radius: 12px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 8px;
  @media (max-width: 360px) {
    width: 30px;
    height: 30px;
    font-size: 18px;
  }
  `;

  /* ------------------------------ BottomSheet -------------------------------- */

  const BottomSheet = styled.div`
    position: fixed;
    bottom: 2vh;
    left: 2vw;
    right: 2vw;
    height: 78vh;
    background: white;
    border-radius: 20px;
    padding: 4vw 5vw 3vw 5vw;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
    animation: slideUp 0.3s ease;
    z-index: 100000;
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow-y: auto;

    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
  `;

  const SheetCloseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    line-height: 0;
    position: absolute;
    top: 0px;
    right: 10px;
  `;

  const SheetCloseImg = styled.img`
    width: 56px;
    height: 56px;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
  `;

  const Input = styled.input`
    width: 100%;
    padding: 12px 14px;
    font-size: 16px;
    border-radius: 12px;
    border: 1px solid #ccc;
  `;

  const AddConfirmButton = styled.button`
    width: 100%;
    background-color: #4285f4;
    color: white;
    padding: 12px;
    font-size: 16px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    margin-top: 16px;
  `;

  /* ------------------------------ Segment ----------------------------------- */

  const StyledSegment = styled(IonSegment)`
  width: 100%;

  ion-segment-button {
    flex: 1;          /* ✅ 3 boutons prennent chacun 1/3 */
    min-width: 0;     /* ✅ autorise la réduction */
    padding-inline: 6px;
  }

  ion-segment-button ion-label {
    display: block;
    white-space: normal;  
    overflow: visible;  
    text-overflow: clip;
    line-height: 1.1;
    font-size: 13px;
  }

  @media (max-width: 360px) {
    ion-segment-button ion-label {
      font-size: 11px;
    }
  }
`;

  /* --------------------------- Numéros de soutiens -------------------------- */

  const EmergencyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 16px;
  `;

  const SectionBlock = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
  `;

  const SectionHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 6px 4px;
    border-bottom: 3px solid #4285f4;
  `;

  const SectionLabel = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #4285f4;
  margin: 0;

  @media (max-width: 360px) {
    font-size: 12px;
  }
`;
  const SectionIconButton = styled.button`
    width: 30px;
    height: 30px;
    border-radius: 999px;
    border: none;
    background: transparent;

    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `;

  const HeaderIconImg = styled.img`
    width: 44px;
    height: 44px;
    object-fit: contain;
    user-select: none;
  `;

  const CardsColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
  `;

  const PinnedCard = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;

    background: #ffce48;
    border-radius: 16px;

    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    padding: 0px 16px;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 16px;
      border-left: 3px solid #4285f4;
      border-right: 3px solid #4285f4;
      pointer-events: none;
    }
  `;

  const PinnedInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
  `;

  const PinnedTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
  `;

  const PinnedNumber = styled.div`
    font-size: 14px;
  `;

  const PinnedActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0;

    & > button:first-child {
    margin-right: -6px;
  }
  `;

  const SupportCard = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    background: #ffffff;
    border-radius: 16px;

    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    padding: 0px 16px;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 16px;
      border-left: 3px solid #4285f4;
      border-right: 3px solid #4285f4;
      pointer-events: none;
    }
  `;

  const SupportInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  `;

  const SupportTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
  `;

  const SupportNumber = styled.div`
    font-size: 13px;
    opacity: 1.7;
    font-weight: 400;
  `;

  const SupportActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0;

  & > button:first-child {
    margin-right: -6px;
  }
  `;

  const IconImgButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: pointer;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;

    &:active {
      transform: scale(0.97);
    }
  `;

  const IconImg = styled.img`
    width: 70px; 
    height: 70px;
    object-fit: cover;
    pointer-events: none;
    user-select: none;  `;

  const ProchesListBlock = styled.div`
    background: #f5f7ff;
    border-radius: 16px;
    padding: 12px;
    margin-bottom: 10px;
    max-height: 51vh;
    overflow-y: auto;

    display: flex;
    flex-direction: column;
    gap: 12px;
  `;

  const ProcheRow = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #ffffff;
    border-radius: 16px;

    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    padding: 13px 16px;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 16px;
      border-left: 3px solid #4285f4;
      border-right: 3px solid #4285f4;
      pointer-events: none;
    }
  `;
const CheckboxWrapper = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;

  ion-checkbox {
    --size: 22px; /* optionnel : taille confortable */
  }
`;
  const ProcheLeft = styled.div`
    display: flex;
    align-items: center;

    input[type="checkbox"],
    input[type="radio"] {
      width: 16px;
      height: 16px;
    }
  `;

  const ProcheInfo = styled.div`
    display: flex;
    flex-direction: column;

    .name {
      font-size: 14px;
      font-weight: 600;
    }

    .phone {
      font-size: 13px;
      opacity: 1.7;
    }
  `;

  const ProchesEmpty = styled.div`
    font-size: 13px;
    opacity: 0.7;
    padding: 6px 2px;
  `;

  const SaveButton = styled(AddConfirmButton)`
    margin-top: auto;
    margin-bottom: 70px;
    border-radius: 20px;
  `;

  const AddProcheButton = styled(AddConfirmButton)`
    margin-top: 12px;
    border-radius: 20px;
  `;

  const AddProcheToggleButton = styled(AddConfirmButton)`
    margin-top: auto;
    margin-bottom: 70px;
    border-radius: 20px;
  `;

  const AddBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: auto;
    margin-bottom: 70px;
  `;

  const ToggleWrapper = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
  `;
const SimpleTitle = styled.div`
  font-size: 15px;
  font-weight: 400;
`;